import Dexie from 'dexie';

/**
 * Offline storage on this device (IndexedDB via Dexie), spec §4.10:
 *   kv         catalog, printers, signed-in profile, offline permit, invoice series, sync times
 *   customers  recent customers for phone lookup without internet
 *   outbox     work done offline, waiting to sync, oldest first. kind: 'bill' (sale, keyed by
 *              clientUuid: a completion replaces a waiting hold of the same order), 'hold' (open
 *              order) or 'kot' (kitchen screen tap)
 *   holds      open orders this device can resume offline: the server's (refreshed while online)
 *              and the ones held here offline (local: true until they sync)
 *   staff      offline user switching: each person's profile and a PIN check made on this device
 *   kitchen    kitchen tickets of orders sent offline from this browser (local kitchen screen)
 * Only one business is kept: signing in to another business on this browser clears it.
 */
export const db = new Dexie('shripos');
db.version(1).stores({
  kv: 'key',
  customers: 'id, phone, name',
  outbox: 'clientUuid, status, createdAt',
});
db.version(2).stores({
  kv: 'key',
  customers: 'id, phone, name',
  outbox: 'clientUuid, status, createdAt, kind',
  holds: 'clientUuid, createdAt',
  staff: 'userId',
  kitchen: 'id, status, createdAt',
});

export async function kvGet(key) {
  try {
    return (await db.kv.get(key))?.value ?? null;
  } catch {
    return null; // private mode / storage blocked: behave as "nothing cached"
  }
}

export async function kvSet(key, value) {
  try {
    await db.kv.put({ key, value, at: Date.now() });
  } catch {
    /* storage full or blocked: the app still works online */
  }
}

/** Keep the cache for one business; wipe it when a different one signs in here. */
export async function claimDb(tenantId) {
  const owner = await kvGet('owner');
  if (owner && owner !== tenantId) {
    const pending = await db.outbox
      .where('status')
      .anyOf('pending', 'failed')
      .count()
      .catch(() => 0);
    // Never throw away unsynced work: keep it until it syncs for its own business.
    if (pending) return false;
    await Promise.all([
      db.kv.clear(),
      db.customers.clear(),
      db.outbox.clear(),
      db.holds.clear(),
      db.staff.clear(),
      db.kitchen.clear(),
    ]).catch(() => {});
  }
  await kvSet('owner', tenantId);
  return true;
}
